import type { PublicCountryConfig } from './types.js';

// goszakup.gov.kz and other KZ sources publish region names in Russian. The 17
// oblasts + 3 republican-significance cities (Astana/Almaty/Shymkent; Baikonur
// excluded — special status) live in the DB regions table, seeded per deployment
// (`services/api/prisma/kz-seed.ts`, names from `kato-region.map.ts`).
//
// Their official Kazakh names, paired with the Russian `name` the seed writes. On
// KZ the `regions.name_uz` column holds the KAZAKH name (Region has no `name_kk`,
// the same convention as the KZ categories), so the API's `pickLabel` already
// serves it to `lang=kk` once the column is filled — kz-seed.ts fills it from this
// table. The web-side `localize` below covers the strings that do NOT come through
// `pickLabel`: an organization's free-text `region` (the Russian name) on the
// supplier / buyer / companies pages, and the buyer-region fallback, which returns
// `name_uz ?? name` and so hands a ru page the Kazakh name. Added 2026-10-18: /kk
// showed every region in Russian (audit 17.10).
const KZ_REGION_NAMES: readonly { readonly ru: string; readonly kk: string }[] = [
  { ru: 'Абайская область', kk: 'Абай облысы' },
  { ru: 'Акмолинская область', kk: 'Ақмола облысы' },
  { ru: 'Актюбинская область', kk: 'Ақтөбе облысы' },
  { ru: 'Алматинская область', kk: 'Алматы облысы' },
  { ru: 'Атырауская область', kk: 'Атырау облысы' },
  { ru: 'Восточно-Казахстанская область', kk: 'Шығыс Қазақстан облысы' },
  { ru: 'Жамбылская область', kk: 'Жамбыл облысы' },
  { ru: 'Жетысуская область', kk: 'Жетісу облысы' },
  { ru: 'Западно-Казахстанская область', kk: 'Батыс Қазақстан облысы' },
  { ru: 'Карагандинская область', kk: 'Қарағанды облысы' },
  { ru: 'Костанайская область', kk: 'Қостанай облысы' },
  { ru: 'Кызылординская область', kk: 'Қызылорда облысы' },
  { ru: 'Мангистауская область', kk: 'Маңғыстау облысы' },
  { ru: 'Павлодарская область', kk: 'Павлодар облысы' },
  { ru: 'Северо-Казахстанская область', kk: 'Солтүстік Қазақстан облысы' },
  { ru: 'Туркестанская область', kk: 'Түркістан облысы' },
  { ru: 'Улытауская область', kk: 'Ұлытау облысы' },
  { ru: 'г. Астана', kk: 'Астана қ.' },
  { ru: 'г. Алматы', kk: 'Алматы қ.' },
  { ru: 'г. Шымкент', kk: 'Шымкент қ.' },
];

/** Case/space-insensitive key, so «г.Астана» and «Г. Астана» find their row. */
function regionKey(s: string): string {
  return s.toLowerCase().replace(/\s+/g, '').replace(/ё/g, 'е');
}

const KK_BY_RU = new Map(KZ_REGION_NAMES.map((r) => [regionKey(r.ru), r.kk]));
const RU_BY_KK = new Map(KZ_REGION_NAMES.map((r) => [regionKey(r.kk), r.ru]));

export const kzPublic: PublicCountryConfig = {
  code: 'kz',
  // ru (default, bare paths) + kk (Kazakh, /kk/* prefixed). The Kazakh catalog is
  // a machine-translation first pass pending native review; the ru↔kk switcher
  // renders because LOCALES.length > 1.
  locales: ['ru', 'kk'],
  defaultLocale: 'ru',
  // Search engines get the Russian pages only (owner, 2026-10-18): the Kazakh
  // interface stays for people, but /kk is no longer prerendered, so no ru page
  // names a /kk alternate and the sitemap lists none. See `indexedLocales`.
  indexedLocales: ['ru'],
  regions: {
    /**
     * A region name in the page's language: kk → the Kazakh name, ru → the
     * Russian one, whichever of the two arrives. Anything not in the table (a
     * district, a free-text oddity) is returned unchanged, as before.
     */
    localize(name, locale) {
      if (!name || !name.trim()) return null;
      const key = regionKey(name);
      const mapped = locale === 'kk' ? KK_BY_RU.get(key) : RU_BY_KK.get(key);
      return mapped ?? name;
    },
  },
  currency: {
    code: 'KZT',
    symbol: '₸',
    // Same order/shape as uz.public.ts's ru array so format.ts picks it by locale key
    // with no country branch. (uz ru = ['трлн','млрд','млн','тыс.'].) kk added
    // 2026-10-18 — /kk printed «175 тыс.» in a Kazakh interface: «мың» is the
    // Kazakh thousand; трлн/млрд/млн are written the same in Kazakh.
    compactUnits: {
      ru: ['трлн', 'млрд', 'млн', 'тыс.'],
      kk: ['трлн', 'млрд', 'млн', 'мың'],
    },
    // KZ amounts read «тенге» (KZT), the ru-locale currency word — parallel to
    // UZ's «сум». (₸/тг are alternatives; the word matches the UZ UI convention.)
    // kk = Kazakh «теңге».
    words: { ru: 'тенге', kk: 'теңге' },
  },
  brand: {
    name: 'KazTender.com',
    siteUrl: 'https://kaztender.com',
    supportEmail: 'support@kaztender.com',
    // Owner's call 2026-10-13: contacts on KZ are Telegram and email ONLY — the
    // phone / WhatsApp line (his own Uzbek +998 mobile) is not published here.
    // The Telegram contact is the same support handle the UZ site publishes;
    // change it on this one line if KZ gets its own account.
    supportTelegram: { handle: '@flesteyto', url: 'https://t.me/flesteyto' },
    publishPhone: false,
    // The same teal as UZ — KazTender has always rendered in it.
    palette: { primary: '#0a5c5c', mid: '#0d7070', light: '#e6f0f0' },
  },
  // Country name in the cases the copy uses — ru + kk (Kazakh).
  countryName: {
    ru: { nom: 'Казахстан', gen: 'Казахстана', prep: 'Казахстане' },
    kk: { nom: 'Қазақстан', gen: 'Қазақстанның', prep: 'Қазақстанда' },
  },
  // Web ИНН/БИН form: KZ БИН/ИИН is 12 digits (server keeps the full checksum validate).
  companyIdForm: { label: 'БИН/ИИН', digits: 12 },
  phonePlaceholder: '+7 707 123 45 67',
  // Shapes measured on the KZ base, 2026-10-17 — live lots: 29 664 ЕНС ТРУ
  // `999999.999.999999`, 726 `99.99.99`, 23 longer dotted КПВЭД codes (up to
  // 9 segments); `ikpu_codes` holds the same four shapes. Accepted: ЕНС ТРУ and
  // its prefixes (6-digit class, class + 1–3 digit subclass, + 1–6 digit tail),
  // a 2-digit section, and dotted 1–2 digit segments up to 8 deep. Refused:
  // anything else, e.g. a bare 1-digit code (159 live lots carry one — noise).
  productCodePattern: /^\d{2}(?:\d{4}(?:\.\d{1,3}(?:\.\d{1,6})?)?|(?:\.\d{1,2}){0,8})$/,
  // KZ's own Meta Pixel — a SEPARATE pixel from UZ's (provided 2026-09-16). Its
  // CAPI access token is a server secret in the KZ host .env, not here.
  // ga4MeasurementId '' = GA4 OFF on KZ (no leak into UZ's property; KZ gets its
  // own GA4 later, or stays off).
  analytics: { metaPixelId: '1612532190234629', ga4MeasurementId: '' },
  // KZ runs its own bot since 2026-10-14 (owner): @kaztender_com_bot, whose
  // assistant is Айгерим. Every Telegram surface the Uzbek site has (login
  // widget, connect banners, onboarding step, alerts in the chat) is on. There
  // is NO Kazakh channel, so channel lines and footers render nothing here. The
  // first alert of a search saved before any chat is linked still goes by
  // e-mail — the channel KZ accounts had until now (see MessagingConfig).
  messaging: {
    telegram: true,
    telegramBot: 'kaztender_com_bot',
    telegramChannel: null,
    emailFirstAlert: true,
    // One first lot per saved search (2026-10-22): the create-time send and
    // `POST /me/first-alert` both read the SAVED row, and the second stays silent
    // once the search has a sent match. Measured on KZ over 10 days: 1 of 34 first
    // alerts went out twice (0 s apart, two different lots). Ported from Spain.
    singleFirstAlert: true,
  },
  // priceIntel ON (2026-09-18): goszakup publishes КТРУ codes that flow through the
  // classifier stub, so ~81% of live KZ lots carry an ikpu + procurement_kind — enough
  // for /market, the goods/works/services kind filter, and an /prices band built from
  // goszakup contract award prices (the goszakup-contracts registry harvest; BidHistory
  // stays UZ-only). teamPlans ON since 2026-10-13 (owner): Kazakhstan sells Pro and
  // «Бизнес», monthly — see `payments` below for what is NOT sold.
  // corporateContactGate OFF: KZ's corporate sources (mitwork.kz; Samruk-Kazyna from the
  // zakup.gov.kz single window) publish a buyer БИН and no contact, so gating corporate on
  // a contact would hide the whole layer (~1,381 mitwork lots then). Exempt it like
  // ifi/supply — the value is the notice + AI summary, not a contact card.
  // conversionLandings OFF (2026-10-13): /start and /ip exist only as Uzbek copy —
  // «Все тендеры Узбекистана», prices in so'm, Payme, the +998 phone — and were
  // being served, prerendered and sitemapped on kaztender.com. Off until KZ has
  // landing content of its own; the paths redirect to the catalogue meanwhile.
  features: {
    priceIntel: true,
    teamPlans: true,
    corporateContactGate: false,
    conversionLandings: false,
    // ИИ-онбординг ON for everyone (owner, 2026-10-15: «ИИ-онбординг врубай на
    // КЗ») — no PostHog flag, no A/B: the onboarding search step asks «Что вы
    // поставляете?» and the server proposes a saved search from KZ's own
    // categories, niches, ЕНС ТРУ codes and regions.
    aiOnboarding: true,
    // Owner, 2026-10-15: a Pro teaser shows only what Pro then shows.
    honestTeasers: true,
    // No supply-request source in KZ `sourceKeys` → no «Кооперация» chip.
    supplyScope: false,
  },
  // The /ifi chips: the institutions in KZ `sourceKeys` (kz.ts) that have lots,
  // in the page's order. No UzRD and no ОБСЕ — Uzbek-only sources the strip used
  // to offer here with a 0 (audit 2026-10-16). No AIIB either: it IS harvested
  // for KZ, but carried 0 KZ lots at the 2026-10-17 audit, so its chip only ever
  // led to an empty list. Put it back here when it has lots; the harvest is
  // unaffected (that is `sourceKeys`).
  ifiSources: ['worldbank', 'adb', 'ungm', 'undp', 'isdb', 'ebrd'],
  // The /catalog chips (owner, 2026-10-19: «сортировка тендеров по источнику для
  // ВСЕХ тендеров, а не только международных»): every non-ifi platform KZ
  // ingests — goszakup, СК-Фармация and med.ecc.kz (medical organisations, since
  // 2026-10-19) are `gov`; Самрук-Казына, Mitwork, ERG (torgi.erg.kz), Qarmet
  // (qpartners.kz), Tizilim (subsoil users), Caspy Tender (td.ccx.kz, natural
  // monopolies), EurasianTech and ЕТС-Тендер (B2B platforms) `corporate` — all inside
  // the catalogue's default gov+corporate scope. The 2026-10-19 additions joined with
  // their harvesters.
  govSources: [
    'goszakup',
    'samruk',
    'mitwork',
    'skpharmacy',
    'medecc',
    'erg',
    'qarmet',
    'tizilim',
    'ccx',
    'eurasiantech',
    'ets-tender',
  ],
  // Owner's decisions, 2026-10-13. Kazakhstan pays through Stripe: one button per
  // plan that leaves for Stripe Checkout (a monthly subscription in tenge) — the
  // Uzbek card sheet, the gateway chooser and the single payment are never
  // opened here. MONTHLY ONLY for now (no «Год» tab), no payment by invoice, no
  // ИП tier: the two plans sold are Pro and «Бизнес». The only marks that may be
  // printed are Visa and Mastercard. Cards are accepted by the owner's
  // US company; the operator in the requisites stays the Uzbek one.
  payments: {
    checkout: 'stripe',
    yearlyPlans: false,
    bankInvoice: false,
    ipPlan: false,
    // Owner, 2026-10-20: the Stripe mark goes — «просто Visa, Mastercard».
    marks: ['visa', 'mastercard'],
    cardPayee: {
      name: 'AI CRM Inc',
      address: '251 Little Falls Drive, Wilmington, New Castle County, DE 19808, USA',
      phone: '302-636-5401',
      processor: 'Stripe',
    },
  },
};
